import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDebouncedFn } from '@salvapp/ui-components';
import { InternalApiNoteDraft } from '../api-internal';
import useBeforeUnload from '../common/useBeforeUnload';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const DEBOUNCE_DELAY_MS = 3000;
const INTERVAL_BACKUP_MS = 60000;
const NOOP = () => false;

export interface DraftMutationOperations {
  create: (content: string) => Promise<InternalApiNoteDraft>;
  update: (id: number, content: string) => Promise<InternalApiNoteDraft>;
}

interface UseAutoSaveDraftOptions {
  mutations: DraftMutationOperations;
  existingDraft?: InternalApiNoteDraft | null;
  enabled: boolean;
}

interface AutoSaveState {
  status: AutoSaveStatus;
  lastSavedAt: Date | null;
  currentDraftId: number | null;
  errorMessage?: string;
}

interface UseAutoSaveDraftReturn {
  content: string;
  setContent: (content: string) => void;
  saveState: AutoSaveState;
  isDirty: boolean;
  currentDraftId: number | null;
  save: () => Promise<void>;
  cancel: () => Promise<void>;
  discard: () => Promise<number | null>;
  resume: () => void;
}

export const useAutoSaveNoteDraft = ({
  mutations,
  existingDraft,
  enabled,
}: UseAutoSaveDraftOptions): UseAutoSaveDraftReturn => {
  const [content, setContentState] = useState(existingDraft?.content ?? '');
  const [saveState, setSaveState] = useState<AutoSaveState>({
    status: existingDraft ? 'saved' : 'idle',
    lastSavedAt: existingDraft?.updatedTime ? new Date(existingDraft.updatedTime) : null,
    currentDraftId: existingDraft?.id ?? null,
  });
  const [isDirty, setIsDirty] = useState(false);

  const lastSavedContentRef = useRef(existingDraft?.content ?? '');
  const intervalTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);
  const discardedRef = useRef(false);
  const pendingSaveRef = useRef<Promise<void>>(Promise.resolve());
  const currentDraftIdRef = useRef<number | null>(existingDraft?.id ?? null);
  const contentRef = useRef(content);
  const isDirtyRef = useRef(isDirty);

  // Initialize content from existing draft
  useEffect(() => {
    if (existingDraft) {
      setContentState(existingDraft.content);
      lastSavedContentRef.current = existingDraft.content;
      currentDraftIdRef.current = existingDraft.id;
      setSaveState({
        status: 'saved',
        lastSavedAt: existingDraft.updatedTime ? new Date(existingDraft.updatedTime) : null,
        currentDraftId: existingDraft.id,
      });
    }
  }, [existingDraft]);

  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  const performSave = useCallback(
    async (contentToSave: string) => {
      if (!enabled || isSavingRef.current || discardedRef.current) return;
      if (!contentToSave.trim()) return;
      if (contentToSave === lastSavedContentRef.current) return;

      isSavingRef.current = true;
      setSaveState((prev) => ({ ...prev, status: 'saving' }));

      const saveOperation = (async () => {
        try {
          let savedDraft: InternalApiNoteDraft;

          if (currentDraftIdRef.current) {
            savedDraft = await mutations.update(currentDraftIdRef.current, contentToSave);
          } else {
            savedDraft = await mutations.create(contentToSave);
          }

          lastSavedContentRef.current = contentToSave;
          currentDraftIdRef.current = savedDraft.id;
          isDirtyRef.current = false;
          setIsDirty(false);
          setSaveState({
            status: 'saved',
            lastSavedAt: new Date(),
            currentDraftId: savedDraft.id,
          });
        } catch (_error) {
          setSaveState((prev) => ({
            ...prev,
            status: 'error',
            errorMessage: 'Failed to save draft',
          }));
        } finally {
          isSavingRef.current = false;
        }
      })();

      // Safe to overwrite: isSavingRef prevents concurrent performSave calls
      pendingSaveRef.current = saveOperation;
      await saveOperation;
    },
    [enabled, mutations]
  );

  const performSaveRef = useRef(performSave);
  performSaveRef.current = performSave;

  const { debouncedFn: debouncedSave, cancel: cancelDebounce } = useMemo(
    () =>
      getDebouncedFn((contentToSave: string) => {
        performSaveRef.current(contentToSave);
      }, DEBOUNCE_DELAY_MS),
    []
  );

  const setContent = useCallback(
    (newContent: string) => {
      setContentState(newContent);
      setIsDirty(newContent !== lastSavedContentRef.current);

      if (!enabled) return;

      if (newContent.trim()) {
        debouncedSave(newContent);
      } else {
        cancelDebounce();
      }
    },
    [enabled, debouncedSave, cancelDebounce]
  );

  // 60-second interval backup
  useEffect(() => {
    if (!enabled) return;

    intervalTimerRef.current = setInterval(() => {
      if (!discardedRef.current && isDirtyRef.current && contentRef.current.trim()) {
        performSaveRef.current(contentRef.current);
      }
    }, INTERVAL_BACKUP_MS);

    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
      }
    };
  }, [enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelDebounce();
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
      }
      if (!discardedRef.current && isDirtyRef.current && contentRef.current.trim()) {
        performSaveRef.current(contentRef.current);
      }
    };
  }, [cancelDebounce]);

  const shouldBlockUnload = useCallback(() => isDirtyRef.current && !!contentRef.current.trim(), []);

  useBeforeUnload(enabled ? shouldBlockUnload : NOOP);

  // Manual save function
  const save = useCallback(async () => {
    cancelDebounce();
    await performSave(content);
  }, [content, performSave, cancelDebounce]);

  const cancel = useCallback(async () => {
    cancelDebounce();
    // Flush unsaved content before closing
    if (isDirtyRef.current && contentRef.current.trim()) {
      await performSaveRef.current(contentRef.current);
    }
  }, [cancelDebounce]);

  const discard = useCallback(async (): Promise<number | null> => {
    cancelDebounce();
    if (intervalTimerRef.current) {
      clearInterval(intervalTimerRef.current);
      intervalTimerRef.current = null;
    }
    discardedRef.current = true;
    try {
      await pendingSaveRef.current;
    } catch {
      // Ignore errors from in-flight save
    }
    return currentDraftIdRef.current;
  }, [cancelDebounce]);

  const resume = useCallback(() => {
    discardedRef.current = false;
    if (enabled && !intervalTimerRef.current) {
      intervalTimerRef.current = setInterval(() => {
        if (!discardedRef.current && isDirtyRef.current && contentRef.current.trim()) {
          performSaveRef.current(contentRef.current);
        }
      }, INTERVAL_BACKUP_MS);
    }
  }, [enabled]);

  return {
    content,
    setContent,
    saveState,
    isDirty,
    currentDraftId: saveState.currentDraftId,
    save,
    cancel,
    discard,
    resume,
  };
};
